import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, Table, TableHead, TableBody, TableRow, TableCell, Typography, Chip, Divider,
  TextField, Grid, Button,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../api/client';
import { ClientPicker } from '../../components/fields/ClientPicker';
import { PageHeader, PageActions } from '../../components/common/PageHeader';

import { formatLocalDate } from '../../utils/date';
function money(n: number) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}

const TYPE_COLOR: Record<string, 'info' | 'success' | 'warning' | 'secondary'> = {
  INVOICE: 'info', PAYMENT: 'success', RETURN: 'warning', EXCHANGE: 'secondary',
};

// F4: one client's full statement — every SalesInvoice (debit), InvoicePayment (credit, incl.
// F2's standalone on-account payments), SalesReturn (credit), and OldGoldExchange (credit) that
// ever moved their Client.currentBalance, in one chronological list with a running balance.
export function ClientLedger() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [from, setFrom] = useState(formatLocalDate()); // defaults to today
  const [to, setTo] = useState(formatLocalDate()); // defaults to today

  const { data, isLoading } = useQuery({
    queryKey: ['client-ledger', clientId, from, to],
    queryFn: () => apiGet<any>(`/finance/reports/client-ledger/${clientId}`, {
      ...(from && { from }), ...(to && { to }),
    }),
    enabled: !!clientId,
  });

  const rows: any[] = data?.rows ?? [];
  // Shares ClientPicker's cache entry for the chosen client.
  const { data: chosen } = useQuery({
    queryKey: ['client-one', clientId],
    queryFn: () => apiGet<any>('/orders/clients', { id: clientId, limit: 1 }),
    enabled: !!clientId, staleTime: 60_000,
  });
  const selected = clientId ? (Array.isArray(chosen) ? chosen[0] : chosen?.data?.[0]) ?? null : null;

  return (
    <Box>
      <PageHeader title="Client Ledger" />

      <Card sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <ClientPicker size="small" label="Client" value={clientId ?? ''}
              onChange={(id) => navigate(id ? `/finance/client-ledger/${id}` : '/finance/client-ledger')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="From" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="To" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button fullWidth onClick={() => { setFrom(''); setTo(''); }}>Clear</Button>
          </Grid>
          <Grid item sx={{ ml: 'auto', alignSelf: 'center' }}>
            <PageActions actions={data ? [{ label: 'Print', icon: <Print />, onClick: () => window.print() }] : []} />
          </Grid>
        </Grid>
      </Card>

      {!clientId ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Pick a client to view their statement</Typography>
        </Card>
      ) : (
        <Card>
          {selected && (
            <Box sx={{ p: 2 }}>
              <Typography fontWeight={700}>{selected.clientCode} — {selected.name}</Typography>
              <Typography variant="body2" color="text.secondary">{selected.type}</Typography>
            </Box>
          )}
          <Divider />
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>Loading...</TableCell></TableRow>
                ) : (
                  <>
                    <TableRow sx={{ bgcolor: '#F7F8FC' }}>
                      <TableCell colSpan={5} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Opening Balance</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{money(data?.openingBalance ?? 0)}</TableCell>
                    </TableRow>
                    {rows.map((r) => (
                      <TableRow key={`${r.type}-${r.refNumber}`} hover>
                        <TableCell>{new Date(r.date).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell><Chip label={r.type} size="small" color={TYPE_COLOR[r.type] ?? 'default'} /></TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.debit > 0 ? money(r.debit) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.credit > 0 ? money(r.credit) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{money(r.balance)}</TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No transactions in this period</TableCell></TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </Box>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 3, bgcolor: '#EEF1FA' }}>
            <Typography fontWeight={700} sx={{ fontFamily: 'monospace' }}>
              Closing Balance: {money(data?.closingBalance ?? 0)}
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );
}
